import { Info, User, WarningCircle } from "@phosphor-icons/react";
import clsx from "clsx";
import { Link } from "react-router-dom";

import LeadLabels from "common/components/LeadLabels";
import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import { Button } from "common/components/ui/Button";
import { Dialog, OverlayDialogContent } from "common/components/ui/Dialog";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import { Sidebar } from "common/layouts/WorkspaceLayout";

import useLead from "../../../../pages/leads/datahooks/useLead";
import {
  About,
  LeadHeading,
  LeadNotes,
  LeadOf,
  LeadSectionContainer,
  LeadSource,
  LinkedInSubscription,
  MessageLeadButton,
} from "..";
import CurrentExperience from "./CurrentExperience";

interface LeadPreviewProps {
  leadId: string;
  isOpen: boolean;
  onClose: () => void;
  isWithSendMessage?: boolean;
}

export default function LeadPreview({
  leadId,
  isOpen,
  onClose,
  isWithSendMessage = true,
}: LeadPreviewProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { lead, isLoadingLead, leadError } = useLead({ profileId: leadId });

  const content = leadError ? (
    <div className="flex items-center gap-x-2 m-auto text-red-500">
      <WarningCircle weight="fill" size={24} />
      <p className="text-lg-medium">Error loading lead</p>
    </div>
  ) : (
    <>
      <div
        className={clsx(
          "flex pl-16 lg:pl-4 lg:pr-10 pt-3 pr-4 lg:pt-2.5 py-2.5 border-b",
          isLoadingLead ? "border-b-black-200" : "border-b-transparent",
        )}
      >
        <LeadHeading lead={lead} isLoading={isLoadingLead} size="md" />
      </div>
      {lead && !lead.is_lead && (
        <div className="flex px-4 py-2 border-b border-b-black-200">
          <div className="px-4 py-3 flex border rounded-3xl shadow-sm border-yellow-500 text-xs gap-x-2">
            <Info weight="fill" size={16} />
            This person is not a lead yet - some data and options are not
            available
          </div>
        </div>
      )}
      {!isLoadingLead && lead?.is_lead && (
        <div className="flex px-4 gap-x-2.5 py-2 border-b border-b-black-200">
          <Button
            variant="secondary-purple"
            leftIcon={<User weight="fill" />}
            className="flex-1"
            asChild
            size="sm"
          >
            <Link
              to={`/workspaces/${workspaceId}/leads/${leadId}`}
              target="_blank"
            >
              See full profile
            </Link>
          </Button>
          <RenderIf condition={isWithSendMessage}>
            <MessageLeadButton
              lead={lead}
              isLoading={isLoadingLead}
              size="sm"
            />
          </RenderIf>
        </div>
      )}
      {lead?.origins && <LeadSource origins={lead?.origins} />}
      <LinkedInSubscription
        isLoading={isLoadingLead}
        hasPremium={lead?.premium}
      />
      {lead?.lead_of && <LeadOf leadOf={lead.lead_of} />}
      {lead?.is_lead && (
        <RestrictedComponent visibleForPermissions={["leads_full"]}>
          <LeadSectionContainer isColumn title="Labels" hasBorder>
            <LeadLabels labels={lead.labels} profileId={leadId} align="start" />
          </LeadSectionContainer>
        </RestrictedComponent>
      )}
      <About lead={lead} isLoading={isLoadingLead} hasBorder={false} />
      <CurrentExperience
        //this is used to reset the selectedExperienceIndex state inside CurrentExperience when the lead changes
        key={lead?.id}
        currentExperience={lead ? lead.current_experience : []}
        isLoading={isLoadingLead}
      />
      {lead?.is_lead && (
        <RestrictedComponent visibleForPermissions={["leads_full"]}>
          <LeadSectionContainer isColumn title="Notes" hasBorder={false}>
            <LeadNotes notes={lead.notes} profileId={leadId} />
          </LeadSectionContainer>
        </RestrictedComponent>
      )}
    </>
  );

  if (isDesktop) {
    return (
      <Sidebar isOpen={isOpen} onClose={onClose}>
        {content}
      </Sidebar>
    );
  }
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <OverlayDialogContent>{content}</OverlayDialogContent>
    </Dialog>
  );
}
